import { type FC, useState } from "react";

import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItems } from "@/shared/hooks/useEstateItems";
import { useAssetEstimate } from "@/shared/hooks/useAssetEstimate";
import { supabase } from "@/shared/lib/supabase";
import { uploadSupportingDocument } from "@/shared/utils/uploadSupportingDocument";
import { AddItemLayout } from "@/pages/postloss/modules/components/AddItemLayout";
import { EstimateLoadingAnimation } from "@/shared/components/EstateItems/EstimateLoadingAnimation";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";
import { sentryUtils } from "@/shared/utils/sentry";

import { useFormWithErrors } from "@/shared/hooks/useFormWithErrors";

import {
  PropertyForm,
  EMPTY_PROPERTY_FORM,
  buildItemName,
  validatePropertyFields,
  propertyToItemFields,
  type PropertyFormValues,
  type PropertyFieldErrors,
} from "./PropertyForm";

export const AddProperty: FC<FlowCustomComponentProps> = ({ goToScreen, answers }) => {
  const { person, user } = useAppContext();
  const estateId = person?.estate_id;
  const { addItem, isAddingItem } = useEstateItems(estateId);
  const estimateMutation = useAssetEstimate();

  const { form, fieldErrors, setFieldErrors, updateForm } = useFormWithErrors<
    PropertyFormValues,
    PropertyFieldErrors
  >(EMPTY_PROPERTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    if (isSubmitting) return;

    const errors = validatePropertyFields(form);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    if (!estateId) return;

    setIsSubmitting(true);
    setError(null);
    setFieldErrors({});

    try {
      let sourceDocumentId: number | undefined;

      if (form.deedFile) {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session) throw new Error("Not authenticated");
        sourceDocumentId = await uploadSupportingDocument(
          form.deedFile,
          estateId,
          session.access_token,
          "property_deed",
        );
      }

      const address = buildItemName(form);
      const fields = propertyToItemFields(form);

      let estimatedBalance = fields.balance;
      let metadata: Record<string, unknown> = { ...fields.metadata };

      try {
        const estimate = await estimateMutation.mutateAsync({
          estateId,
          assetType: "real-estate",
          data: { address },
        });

        if (estimate.estimatedValue > 0) {
          estimatedBalance = estimate.estimatedValue;
          metadata = { ...metadata, value_source: "smart_estimator" };
        }
      } catch (err) {
        sentryUtils.captureError(
          err,
          "Property estimate failed",
          { estateId: person?.estate_id },
          "warning",
        );
      }

      await addItem({
        estate_id: estateId,
        category: "Properties",
        classification: "asset",
        item_name: address,
        item_type: fields.item_type,
        balance: estimatedBalance,
        ownership: fields.ownership,
        metadata,
        source_document_id: sourceDocumentId,
      });

      goToScreen("add_properties_step", {
        ...answers,
        properties_review: "has_items",
        add_property: "done",
      });
    } catch (err) {
      setError("Failed to add item. Please try again.");
      sentryUtils.captureError(err, "AddProperty submission failed", {
        userId: user?.user_id,
        estateId: person?.estate_id,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitting) {
    return (
      <div className="mx-auto flex w-full max-w-[600px] flex-col">
        <EstimateLoadingAnimation assetType="real-estate" />
      </div>
    );
  }

  return (
    <AddItemLayout
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      error={error}
      disabled={isAddingItem}
    >
      <PropertyForm form={form} onChange={updateForm} fieldErrors={fieldErrors} showValue={false} />
    </AddItemLayout>
  );
};
